import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getData } from "../../services/dataService";
import "../../assets/styles/bootstrap.css";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import CoordinatesMap from "../CoordinatesMap.jsx";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { CEMENTERY_LAT, CEMENTERY_LNG } from "../../config/defaultValues";
import Card from "react-bootstrap/Card";
import { CardBody, FloatingLabel } from "react-bootstrap";

const GraveForm = ({ show, onClose, graveID, formType }) => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const [error, setError] = useState(null);
  const [showSelectCoordinatesMap, setShowSelectCoordinatesMap] = useState(false);
  const [graveData, setGraveData] = useState({
    uuid: "",
    identifier: "",
    coordinate: { x: 0, y: 0 },
    graveTypeID: 0,
    centuryID: 0,
    graveMakerID: 0,
    imageIdList: [],
    description: "",
  });

  const [attributes, setAttributes] = useState({
    graveTypes: [],
    centuries: [],
    graveMakers: [],
  });

  const handleShowSelectCoordinates = () => {
    setShowSelectCoordinatesMap(!showSelectCoordinatesMap);
  }

  useEffect(() => {
    const fetchAttributes = async () => {
      try {
        const attributesData = await getData("/graves/attributeLists");
        setAttributes(attributesData);
      } catch (error) {
        setError(error);
      }
    };

    const fetchGraveData = async () => {
      if (formType === "update" && graveID) {
        try {
          const graveData = await getData(`/graves/${graveID}`);
          setGraveData({
            uuid: graveData?.uuid,
            identifier: graveData?.identifier,
            coordinate: graveData?.coordinate || { x: 0, y: 0 },
            graveTypeID: graveData?.graveType?.id || 0,
            centuryID: graveData?.century?.id || 0,
            graveMakerID: graveData?.graveMaker?.id || 0,
            imageIdList: graveData?.imageIdList || [],
            description: graveData?.description || "",
          });
        } catch (error) {
          console.error("Error submitting form: ", error);
          alert(`${t("formSubmitFailedMessage") + error.message}`);
        }
      }
    };

    fetchAttributes();
    fetchGraveData();
  }, [graveID, formType, t]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "coordinateX" || name === "coordinateY") {
      setGraveData((prevState) => ({
        ...prevState,
        coordinate: {
          ...prevState.coordinate,
          [name === "coordinateX" ? "x" : "y"]: parseFloat(value),
        },
      }));
    } else if (["graveTypeID", "centuryID", "graveMakerID"].includes(name)) {
      setGraveData({ ...graveData, [name]: parseInt(value) });
    } else {
      setGraveData({ ...graveData, [name]: value });
    }
  };

  const handleApplyMapCoordinates = (latLng) => {
    handleChange({
      target: {
        name: "coordinateX",
        value: latLng.lat
      }
    })
    handleChange({
      target: {
        name: "coordinateY",
        value: latLng.lng
      }
    })
  }

  const resetGravesData=()=>{
    setGraveData({
      uuid: "",
      identifier: "",
      coordinate: { x: 0, y: 0 },
      graveTypeID: 0,
      centuryID: 0,
      graveMakerID: 0,
      imageIdList: [],
      description: "",
    });
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formType === "create") {
        await axiosPrivate.post(
          "/graves",
          JSON.stringify(graveData));
        resetGravesData();
        setShowSelectCoordinatesMap(false);
      } else if (formType === "update") {
        await axiosPrivate.put(
          "/graves",
          JSON.stringify(graveData)
        );
      }
      onClose(true);
    } catch (error) {
      alert(`${t("formSubmitFailedMessage") + error.message}`);
      console.error("Error submitting form: ", error);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {formType === "create" ? t("createGrave") : t("updateGrave")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row className="mb-3 modal-row">
            <Col className="my-1">
              <Form.Group controlId="identifier">
                <FloatingLabel
                  controlId="parcelNumber"
                  label={t("parcelNumber")}>
                  <Form.Control
                    name="identifier"
                    value={graveData.identifier}
                    onChange={handleChange}
                    type="text"
                    placeholder={""}
                    className={"border-0 modal-background-color"}
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col></Col>
          </Row>
          <Row className="mb-3 modal-row">
            <Col className="my-1">
              <Form.Group as={Col} controlId="coordinateX">
                <FloatingLabel
                  controlId="coordinateX"
                  label={t("coordinateX")}>
                  <Form.Control
                    name="coordinateX"
                    value={graveData.coordinate.x || CEMENTERY_LAT}
                    onChange={handleChange}
                    type="number"
                    placeholder={""}
                    className={"border-0 modal-background-color"}
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col className="my-1">
              <Form.Group as={Col} controlId="coordinateY">
                <FloatingLabel
                  controlId="coordinateY"
                  label={t("coordinateY")}>
                  <Form.Control
                    name="coordinateY"
                    value={graveData.coordinate.y || CEMENTERY_LNG}
                    onChange={handleChange}
                    type="number"
                    placeholder={""}
                    className={"border-0 modal-background-color"}
                  />
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3 modal-row">
            <Form.Group as={Col} controlId="selectCoordinates">
              <Form.Label>{t("selectCoordinates")}</Form.Label>
                <div className="d-flex align-items-center">
                  <Button
                    variant="outline-dark"
                    onClick={handleShowSelectCoordinates}
                    className={"shadow border-0 p-2 px-5"}
                  >
                    <i className="bi bi-geo-alt me-2"></i>
                    {t("showMap")}
                  </Button>
                </div>
                {showSelectCoordinatesMap && (
                  <Card className="my-4 mb-1 card-component align-self-center map-background-color">
                    <CardBody style={{
                      border: "solid 3px white",
                      boxShadow: "rgba(100, 100, 111, 0.5) 0px 7px 29px 0px",
                      margin: 0,
                      padding: 0,
                      width: "406px",
                      height: "406px",
                    }}>
                      <CoordinatesMap
                        lat={graveData.coordinate.x}
                        lng={graveData.coordinate.y}
                        handleApplyMapCoordinates={handleApplyMapCoordinates}
                        isMarkerDraggable={true}
                        style={{ width: "400px", height: "400px" }}
                      />
                    </CardBody>
                  </Card>
                )}
            </Form.Group>
          </Row>
          <Row className="mb-3 modal-row">
            <Col className="my-1">
              <Form.Group as={Col} controlId="graveType">
                <FloatingLabel
                  controlId="graveType"
                  label={t("graveType")}>
                  <Form.Select
                    name="graveTypeID"
                    value={(graveData.graveTypeID)?(graveData.graveTypeID):(0)}
                    onChange={handleChange}
                    className={"border-0 modal-background-color"}
                  >
                    <option value={0}>{t("choose")}</option>
                    {attributes.graveTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.typeName}
                      </option>
                    ))}
                  </Form.Select>
                </FloatingLabel>
              </Form.Group>
            </Col>
            <Col className="my-1">
              <Form.Group as={Col} controlId="century">
                <FloatingLabel
                  controlId="century"
                  label={t("century")}>
                  <Form.Select
                    name="centuryID"
                    value={(graveData.centuryID)?(graveData.centuryID):(0)}
                    onChange={handleChange}
                    className={"border-0 modal-background-color"}
                  >
                    <option value={0}>{t("choose")}</option>
                    {attributes.centuries.map((century) => (
                      <option key={century.id} value={century.id}>
                        {century.century}
                      </option>
                    ))}
                  </Form.Select>
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3 modal-row">
            <Col className="my-1">
              <Form.Group as={Col} controlId="graveMaker">
                <FloatingLabel
                  controlId="graveMaker"
                  label={t("graveMaker")}>
                  <Form.Select
                    name="graveMakerID"
                    value={(graveData.graveMakerID)?(graveData.graveMakerID):(0)}
                    onChange={handleChange}
                    className={"border-0 modal-background-color"}
                  >
                    <option value={0}>{t("choose")}</option>
                    {attributes.graveMakers.map((graveMaker) => (
                      <option key={graveMaker.id} value={graveMaker.id}>
                        {graveMaker.name}
                      </option>
                    ))}
                  </Form.Select>
                </FloatingLabel>
              </Form.Group>
            </Col>
          </Row>
          <Row className="modal-row">
            <Col className="my-1">
              <Form.Group as={Col} controlId="description">
                <Form.Label>{t("description")}</Form.Label>
                <Form.Control
                  name="description"
                  value={graveData.description}
                  onChange={handleChange}
                  as="textarea"
                  rows={8}
                  className={"border-0 modal-background-color"}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
        {error && <div className="text-danger">{error.message}</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-dark"
          onClick={onClose}
          className={"shadow border-0 p-2 px-5"}
        >
          {t("close")}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          className={"shadow border-0 p-2 px-5"}
        >
          {t("submit")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default GraveForm;
